import { SOFIA_THREAD_QUERY_PARAM } from "@/constants/sofia"

import {
  buildWorkspacePath,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

function asUrl(path: string | null | undefined): URL | null {
  if (!path) {
    return null
  }
  try {
    return new URL(path, "https://coval.local")
  } catch {
    return null
  }
}

export function isSofiaHomePath(path: string | null | undefined): boolean {
  const url = asUrl(path)
  if (!url) {
    return false
  }
  const workspace = parseWorkspaceRoute(url.pathname)
  if (workspace) {
    return workspace.relativePath === "/home"
  }
  const legacy = parseLegacyOrganizationRoute(url.pathname)
  if (legacy) {
    return legacy.relativePath === "/home"
  }
  return url.pathname === "/home"
}

/**
 * True on any page of the Sofia Home family: the landing and saved Notebook
 * pages. These pages own the visible Sofia surface, so the docked panel never
 * paints beside them.
 */
export function isSofiaSurfacePath(path: string | null | undefined): boolean {
  const url = asUrl(path)
  if (!url) {
    return false
  }
  const relative =
    parseWorkspaceRoute(url.pathname)?.relativePath ??
    parseLegacyOrganizationRoute(url.pathname)?.relativePath ??
    url.pathname
  return (
    relative === "/home" ||
    relative === "/home/notebooks" ||
    relative.startsWith("/home/notebooks/")
  )
}

/**
 * Whether the path is anywhere in Sofia's Home section — the briefing, a
 * full-page conversation, or a page hung below Home such as investigation
 * history or a notebook. `isSofiaHomePath` answers only for Home itself, and
 * `isSofiaSurfacePath` only for the pages that own the visible surface; this is
 * the wider question of whether the user is still inside Sofia, and it is what
 * unfolds Sofia's own rows under the sidebar's entry.
 */
export function isSofiaHomeSection(path: string | null | undefined): boolean {
  const url = asUrl(path)
  if (!url) {
    return false
  }
  const inSection = (relativePath: string) =>
    relativePath === "/home" || relativePath.startsWith("/home/")
  const workspace = parseWorkspaceRoute(url.pathname)
  if (workspace) {
    return inSection(workspace.relativePath)
  }
  const legacy = parseLegacyOrganizationRoute(url.pathname)
  if (legacy) {
    return inSection(legacy.relativePath)
  }
  return inSection(url.pathname)
}

export function getSofiaHomeThreadId(
  path: string | null | undefined,
): string | null {
  const url = asUrl(path)
  if (!url || !isSofiaHomePath(path)) {
    return null
  }
  return url.searchParams.get(SOFIA_THREAD_QUERY_PARAM)?.trim() || null
}

function homePathFor(
  currentPath: string | null | undefined,
  organizationSlug: string | null,
): string {
  const url = asUrl(currentPath)
  const workspace = parseWorkspaceRoute(url?.pathname ?? "")
  if (workspace) {
    return buildWorkspacePath(
      workspace.organizationSlug,
      workspace.workspaceId,
      "/home",
    )
  }
  const legacy = parseLegacyOrganizationRoute(url?.pathname ?? "")
  if (legacy) {
    return `/${legacy.organizationSlug}/home`
  }
  return organizationSlug ? `/${organizationSlug}/home` : "/home"
}

export function buildSofiaHomeThreadPath(
  currentPath: string | null | undefined,
  organizationSlug: string | null,
  threadId: string,
): string {
  const currentHome = isSofiaHomePath(currentPath) ? asUrl(currentPath) : null
  const url =
    currentHome ??
    new URL(homePathFor(currentPath, organizationSlug), "https://coval.local")
  url.searchParams.set(SOFIA_THREAD_QUERY_PARAM, threadId)
  return `${url.pathname}${url.search}${url.hash}`
}

export function buildSofiaHomeLandingPath(
  currentPath: string | null | undefined,
  organizationSlug: string | null,
): string {
  if (!isSofiaHomePath(currentPath)) {
    return homePathFor(currentPath, organizationSlug)
  }
  const url = asUrl(currentPath)
  if (!url) {
    return homePathFor(currentPath, organizationSlug)
  }
  url.searchParams.delete(SOFIA_THREAD_QUERY_PARAM)
  return `${url.pathname}${url.search}${url.hash}`
}

export function buildSofiaInvestigationHistoryPath(
  currentPath: string | null | undefined,
  organizationSlug: string | null,
): string {
  return `${homePathFor(currentPath, organizationSlug)}/investigations`
}

export function buildSofiaNotebooksPath(
  currentPath: string | null | undefined,
  organizationSlug: string | null,
  notebookId?: string,
): string {
  const base = `${homePathFor(currentPath, organizationSlug)}/notebooks`
  return notebookId ? `${base}/${encodeURIComponent(notebookId)}` : base
}
